'use client';

import { cn } from '@/lib/ui/utils';
import type { PaymentProviderId } from '@/payment/provider-list';
import { PAYMENT_PROVIDER_RENDER_ORDER } from '@/payment/provider-list';

export type ProviderCheckoutButtonClasses = {
  buttonGroup: string;
  checkoutError: string;
  providerButton: string;
  providerButtonLabel: string;
  providerStripeButton: string;
  providerStripeLabel: string;
  providerStripeLogoWrap: string;
  providerLogoStripe: string;
  providerStripeSecondaryButton: string;
  providerPaypalButton: string;
  providerPaypalSecondaryButton: string;
  providerPaypalLabel: string;
  providerPaypalLogoWrap: string;
  providerLogoPaypal: string;
};

function ProviderCheckoutButton({
  activeProvider,
  analyticsPrefix,
  classes,
  dataAttributes,
  getProviderDataAttributes,
  onSelect,
  paypalLabel,
  provider,
  redirectingLabel,
  stripeLabel,
  providerTone,
  submitting,
}: {
  activeProvider: PaymentProviderId | null;
  analyticsPrefix?: string;
  classes: ProviderCheckoutButtonClasses;
  dataAttributes?: Readonly<Record<string, string>>;
  getProviderDataAttributes?: (
    provider: PaymentProviderId
  ) => Readonly<Record<string, string>>;
  onSelect: (provider: PaymentProviderId) => void;
  paypalLabel: string;
  provider: PaymentProviderId;
  redirectingLabel: string;
  stripeLabel: string;
  providerTone: 'primary' | 'secondary';
  submitting: boolean;
}) {
  const isStripe = provider === 'stripe';
  const label = isStripe ? stripeLabel : paypalLabel;
  const className = isStripe
    ? `${providerTone === 'primary' ? 'cms-button-primary' : 'cms-button-outline'} cms-button-lg ${classes.providerButton} ${classes.providerStripeButton} ${providerTone === 'secondary' ? classes.providerStripeSecondaryButton : ''}`
    : `cms-button-outline cms-button-lg ${classes.providerButton} ${classes.providerPaypalButton} ${providerTone === 'secondary' ? classes.providerPaypalSecondaryButton : ''}`;

  return (
    <button
      aria-busy={(submitting && activeProvider === provider) || undefined}
      {...dataAttributes}
      {...getProviderDataAttributes?.(provider)}
      className={className}
      data-analytics={
        analyticsPrefix
          ? `${analyticsPrefix}_${isStripe ? 'stripe' : 'paypal'}`
          : undefined
      }
      disabled={submitting}
      onClick={() => onSelect(provider)}
      type="button"
    >
      <span
        className={cn(
          classes.providerButtonLabel,
          isStripe ? classes.providerStripeLabel : classes.providerPaypalLabel
        )}
      >
        {submitting && activeProvider === provider ? redirectingLabel : label}
      </span>
      {isStripe ? (
        <span className={classes.providerStripeLogoWrap}>
          <img
            alt="Stripe"
            className={classes.providerLogoStripe}
            decoding="sync"
            fetchPriority="high"
            height={26}
            loading="eager"
            src="/svg/stripe.svg"
            width={104}
          />
        </span>
      ) : (
        <span className={classes.providerPaypalLogoWrap}>
          <img
            alt="PayPal"
            className={classes.providerLogoPaypal}
            decoding="sync"
            fetchPriority="high"
            height={26}
            loading="eager"
            src="/svg/paypal.svg"
            width={104}
          />
        </span>
      )}
    </button>
  );
}

export function ProviderCheckoutButtons({
  activeProvider,
  analyticsPrefix,
  classes,
  dataAttributes,
  getProviderDataAttributes,
  onSelect,
  paypalLabel,
  providerOptions,
  providerOptionsPending,
  redirectingLabel,
  stripeLabel,
  providerTone = 'primary',
  submitting,
  unavailableLabel,
}: {
  activeProvider: PaymentProviderId | null;
  analyticsPrefix?: string;
  classes: ProviderCheckoutButtonClasses;
  dataAttributes?: Readonly<Record<string, string>>;
  getProviderDataAttributes?: (
    provider: PaymentProviderId
  ) => Readonly<Record<string, string>>;
  onSelect: (provider: PaymentProviderId) => void;
  paypalLabel: string;
  providerOptions: readonly PaymentProviderId[] | null;
  providerOptionsPending: boolean;
  redirectingLabel: string;
  stripeLabel: string;
  providerTone?: 'primary' | 'secondary';
  submitting: boolean;
  unavailableLabel: string;
}) {
  const visibleProviders =
    providerOptionsPending || submitting
      ? PAYMENT_PROVIDER_RENDER_ORDER
      : (providerOptions ?? []);

  if (!visibleProviders.length) {
    return <div className={classes.checkoutError}>{unavailableLabel}</div>;
  }

  return (
    <div className={classes.buttonGroup}>
      {visibleProviders.map((provider) => (
        <ProviderCheckoutButton
          key={provider}
          activeProvider={activeProvider}
          analyticsPrefix={analyticsPrefix}
          classes={classes}
          dataAttributes={dataAttributes}
          getProviderDataAttributes={getProviderDataAttributes}
          onSelect={onSelect}
          paypalLabel={paypalLabel}
          provider={provider}
          redirectingLabel={redirectingLabel}
          stripeLabel={stripeLabel}
          providerTone={providerTone}
          submitting={submitting}
        />
      ))}
    </div>
  );
}
